import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { Building2, CheckCircle2, Clock, FileUp, XCircle } from "lucide-react";
import { ApiError } from "../../api/client";
import { corporateApi, type CorporateApplication, type DocumentKind } from "../../api/corporate";
import { LoadError } from "../../components/catalogue/Notices";
import { PageWidth } from "../../components/catalogue/StoreShell";
import type { LatLng } from "../../components/maps/LocationPicker";
import { Button, Card, Icon, Input } from "../../components/ui";
import { formatDay } from "../../lib/dates";
import { ConfirmEmailNote } from "../account/AccountRoute";
import { errorMessage, SESSION_KEY, useCustomerSession } from "../account/session";
import { usePageTitle } from "../catalogue/usePageTitle";
import { SignedInOnly } from "../orders/SignedInOnly";

// Only this page draws a map, so only this page downloads the map library.
const LocationPicker = lazy(() => import("../../components/maps/LocationPicker"));

const APPLICATION_KEY = ["account", "corporate"] as const;

/** PRD screens 34 and 35: apply for a corporate account, then see where it stands. */
export function CorporateRoute() {
  usePageTitle("Corporate account");
  return (
    <SignedInOnly>
      <Corporate />
    </SignedInOnly>
  );
}

function Corporate() {
  const application = useQuery({ queryKey: APPLICATION_KEY, queryFn: corporateApi.get });
  const [reapplying, setReapplying] = useState(false);

  return (
    <PageWidth className="max-w-[860px] pt-8">
      <h1 className="mb-2 type-h1 [font-size:var(--text-2xl)]">Corporate account</h1>
      {application.isPending ? (
        <p className="type-body text-text-muted">Loading…</p>
      ) : application.isError ? (
        <LoadError onRetry={() => application.refetch()} />
      ) : !application.data || (application.data.status === "rejected" && reapplying) ? (
        <ApplicationForm previous={application.data} />
      ) : (
        <Status application={application.data} onReapply={() => setReapplying(true)} />
      )}
    </PageWidth>
  );
}

/** Where the application stands. The wait is a week up to a month, so this page carries it. */
function Status({ application, onReapply }: { application: CorporateApplication; onReapply: () => void }) {
  const percent = application.discount_percent !== null ? `${application.discount_percent}%` : "";
  const content: Record<CorporateApplication["status"], { icon: typeof Clock; tone: string; title: string; body: ReactNode }> = {
    pending: {
      icon: Clock,
      tone: "border-gold-300 bg-surface-tint-gold text-gold-700",
      title: "We're checking your application",
      body: (
        <>
          We check every application carefully, to protect your business and the corporate accounts we already have in your area. It usually takes about a week, and
          at most a month. We'll email you as soon as we've decided. Until then, order as usual at the standard prices.
        </>
      ),
    },
    approved: {
      icon: CheckCircle2,
      tone: "border-green-200 bg-success-100 text-success-700",
      title: `Approved: ${percent} off every part`,
      body: (
        <>
          Every part in the shop shows your company price while you're signed in. Delivery is charged at the standard rate. Need an official receipt with your TIN? Add a
          note when you place the order.
        </>
      ),
    },
    rejected: {
      icon: XCircle,
      tone: "border-[#e8cdc8] bg-danger-100 text-danger-700",
      title: "We couldn't approve this application",
      body: (
        <>
          {application.rejection_reason && <span className="mb-2 block text-text-strong">“{application.rejection_reason}”</span>}
          You can keep ordering at the standard prices. If the reason can be fixed, you're welcome to apply again.
        </>
      ),
    },
    deactivated: {
      icon: XCircle,
      tone: "border-border-default bg-surface-sunken text-ink-600",
      title: "This corporate account is no longer active",
      body: <>Your orders are at the standard prices. If you think this is a mistake, please contact us.</>,
    },
  };
  const shown = content[application.status];

  return (
    <div className="mt-6 flex flex-col gap-6">
      <div role="status" className={`flex items-start gap-3 rounded-md border p-5 ${shown.tone}`}>
        <Icon icon={shown.icon} size={24} className="mt-0.5 flex-none" />
        <div>
          <p className="mb-1 type-h4 [font-size:var(--text-md)]">{shown.title}</p>
          <p className="mb-0 type-body text-text-body">{shown.body}</p>
          {application.status === "rejected" && (
            <Button className="mt-4" onClick={onReapply}>
              Apply again
            </Button>
          )}
        </div>
      </div>
      <Card padding="var(--space-6)">
        <p className="mb-3 type-eyebrow tracking-[var(--tracking-wider)] text-ink-500 uppercase">Your application</p>
        <dl className="m-0 grid gap-x-6 gap-y-2 type-body sm:grid-cols-[180px_minmax(0,1fr)]">
          <Detail label="Business">{application.business_name}{application.trade_name && ` (${application.trade_name})`}</Detail>
          <Detail label="TIN"><span className="type-sku">{application.tin}</span></Detail>
          <Detail label="Store">{`${application.street}, ${application.barangay}, ${application.city}, ${application.province}`}</Detail>
          <Detail label="Contact">{`${application.contact_name} · ${application.phone}`}</Detail>
          <Detail label="Sent">{formatDay(application.submitted_at)}</Detail>
          <Detail label="Documents">{`${application.documents.length} files`}</Detail>
        </dl>
      </Card>
      <Link to="/account" className="self-start type-label">
        Back to your account
      </Link>
    </div>
  );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="type-small text-text-muted">{label}</dt>
      <dd className="m-0 text-text-strong">{children}</dd>
    </>
  );
}

const DOCUMENTS: { kind: Exclude<DocumentKind, "storefront_photo">; label: string; hint: string }[] = [
  { kind: "bir_2303", label: "BIR Form 2303", hint: "Your Certificate of Registration. We take your TIN from it." },
  { kind: "business_permit", label: "Business permit", hint: "The current year's." },
  { kind: "dti_registration", label: "DTI registration", hint: "Your business name registration." },
  { kind: "location_map", label: "Location map", hint: "A sketch or screenshot of how to find the store." },
];
const MAX_PHOTOS = 5;

type Fields = Record<"business_name" | "trade_name" | "tin" | "contact_name" | "phone" | "street" | "barangay" | "city" | "province" | "postal_code", string>;

/** PRD screen 34: the business, the store's pin, and the five kinds of document. */
function ApplicationForm({ previous }: { previous: CorporateApplication | null }) {
  const queryClient = useQueryClient();
  const session = useCustomerSession();
  const user = session.data?.user;
  const [fields, setFields] = useState<Fields>(() => ({
    business_name: previous?.business_name ?? "",
    trade_name: previous?.trade_name ?? "",
    tin: previous?.tin ?? "",
    contact_name: previous?.contact_name ?? "",
    phone: previous?.phone ?? "",
    street: previous?.street ?? "",
    barangay: previous?.barangay ?? "",
    city: previous?.city ?? "",
    province: previous?.province ?? "",
    postal_code: previous?.postal_code ?? "",
  }));
  const [location, setLocation] = useState<LatLng | null>(previous ? { latitude: previous.latitude, longitude: previous.longitude } : null);
  const [files, setFiles] = useState<Partial<Record<DocumentKind, File[]>>>({});
  const [problems, setProblems] = useState<Record<string, string>>({});

  const apply = useMutation({
    mutationFn: () => {
      const form = new FormData();
      for (const [key, value] of Object.entries(fields)) form.append(`company[${key}]`, value);
      form.append("company[latitude]", String(location?.latitude ?? ""));
      form.append("company[longitude]", String(location?.longitude ?? ""));
      for (const { kind } of DOCUMENTS) files[kind]?.forEach((file) => form.append(`documents[${kind}]`, file));
      files.storefront_photo?.forEach((file) => form.append("documents[storefront_photos][]", file));
      return corporateApi.apply(form);
    },
    onSuccess: (application) => {
      queryClient.setQueryData(APPLICATION_KEY, application);
      queryClient.invalidateQueries({ queryKey: SESSION_KEY });
      window.scrollTo({ top: 0 });
    },
  });

  const server = apply.error instanceof ApiError ? apply.error.details : {};
  const fieldError = (key: string) => problems[key] ?? server[key]?.[0];

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found: Record<string, string> = {};
    const required: [keyof Fields, string][] = [
      ["business_name", "Enter the registered business name"],
      ["tin", "Enter the TIN from your BIR 2303"],
      ["contact_name", "Enter who we should talk to"],
      ["phone", "Enter a mobile or phone number"],
      ["street", "Enter the street"],
      ["barangay", "Enter the barangay"],
      ["city", "Enter the city or municipality"],
      ["province", "Enter the province"],
    ];
    for (const [key, message] of required) if (!fields[key].trim()) found[key] = message;
    if (!location) found.latitude = "Put the pin on your store";
    for (const { kind, label } of DOCUMENTS) if (!files[kind]?.length) found[kind] = `Add your ${label}`;
    if (!files.storefront_photo?.length) found.storefront_photo = "Add at least one photo of the storefront";
    setProblems(found);
    if (Object.keys(found).length === 0) apply.mutate();
  };

  const set = (key: keyof Fields) => (event: { target: { value: string } }) => {
    setFields({ ...fields, [key]: event.target.value });
    setProblems(({ [key]: _, ...rest }) => rest);
  };
  const choose = (kind: DocumentKind, chosen: File[]) => {
    setFiles({ ...files, [kind]: chosen });
    setProblems(({ [kind]: _, ...rest }) => rest);
  };
  const general = apply.error && !(apply.error instanceof ApiError && Object.keys(server).length > 0) ? errorMessage(apply.error) : server.documents?.join(" ");

  return (
    <>
      <p className="mb-6 max-w-[640px] type-body text-text-muted">
        A corporate account gets one percentage off every part, set for your business and reviewed once a year. Your company uses one login: this one. We check every
        application, which usually takes about a week and at most a month.
      </p>
      {user?.email_address && !user.email_confirmed && <ConfirmEmailNote email={user.email_address} />}
      {previous?.status === "rejected" && (
        <p className="mb-6 rounded-md bg-surface-sunken px-4 py-3 type-small text-text-body">
          Your details are filled in from last time. Add the documents again, and fix what we mentioned: “{previous.rejection_reason}”
        </p>
      )}

      <form onSubmit={submit} noValidate className="flex flex-col gap-8">
        <Section title="Your business">
          <div className="grid gap-x-5 gap-y-4 sm:grid-cols-2">
            <Input label="Registered business name" value={fields.business_name} onChange={set("business_name")} error={fieldError("business_name")} className="sm:col-span-2" />
            <Input label="Trade name (optional)" hint="If the store goes by another name" value={fields.trade_name} onChange={set("trade_name")} />
            <Input label="TIN" mono placeholder="123-456-789-000" value={fields.tin} onChange={set("tin")} error={fieldError("tin")} hint="As on your BIR 2303" />
            <Input label="Contact person" autoComplete="name" value={fields.contact_name} onChange={set("contact_name")} error={fieldError("contact_name")} />
            <Input label="Mobile number" mono inputMode="tel" autoComplete="tel" placeholder="0917 000 0000" value={fields.phone} onChange={set("phone")} error={fieldError("phone")} />
          </div>
        </Section>

        <Section title="The store">
          <div className="grid gap-x-5 gap-y-4 sm:grid-cols-2">
            <Input label="House number, street, building" value={fields.street} onChange={set("street")} error={fieldError("street")} className="sm:col-span-2" />
            <Input label="Barangay" value={fields.barangay} onChange={set("barangay")} error={fieldError("barangay")} />
            <Input label="City or municipality" value={fields.city} onChange={set("city")} error={fieldError("city")} />
            <Input label="Province" value={fields.province} onChange={set("province")} error={fieldError("province")} />
            <Input label="Postal code (optional)" inputMode="numeric" value={fields.postal_code} onChange={set("postal_code")} />
          </div>
          <div className="mt-5">
            <p className="mb-1 type-label text-text-strong">Where the store is on the map</p>
            <p className="mb-3 type-small text-text-muted">We check this against our corporate accounts nearby.</p>
            <Suspense fallback={<div className="h-[340px] rounded-md bg-surface-sunken" />}>
              <LocationPicker
                value={location}
                onChange={(next) => {
                  setLocation(next);
                  setProblems(({ latitude: _, ...rest }) => rest);
                }}
                error={fieldError("latitude")}
              />
            </Suspense>
          </div>
        </Section>

        <Section title="Documents">
          <p className="mb-4 type-small text-text-muted">Photos or PDFs, up to 10 MB each. Only our staff see them.</p>
          <div className="flex flex-col gap-3">
            {DOCUMENTS.map((document) => (
              <FilePick key={document.kind} label={document.label} hint={document.hint} files={files[document.kind] ?? []} error={fieldError(document.kind)} onChoose={(chosen) => choose(document.kind, chosen.slice(0, 1))} />
            ))}
            <FilePick
              label="Photos of the storefront"
              hint={`The front of the store, from the street. Up to ${MAX_PHOTOS}.`}
              multiple
              files={files.storefront_photo ?? []}
              error={fieldError("storefront_photo")}
              onChoose={(chosen) => choose("storefront_photo", chosen.slice(0, MAX_PHOTOS))}
            />
          </div>
        </Section>

        <div>
          {general && (
            <p role="alert" className="mb-4 rounded-md bg-danger-100 px-4 py-3 type-small text-danger-700">
              {general}
            </p>
          )}
          {Object.keys(problems).length > 0 && (
            <p role="alert" className="mb-4 type-small text-danger-700">
              Some details are missing. They're marked above.
            </p>
          )}
          <Button type="submit" size="lg" icon={Building2} disabled={apply.isPending || !user?.email_confirmed}>
            {apply.isPending ? "Sending…" : "Send application"}
          </Button>
        </div>
      </form>
    </>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-4 type-h3 [font-size:var(--text-lg)]">{title}</h2>
      {children}
    </section>
  );
}

/** One kind of document: a button to choose it, and what was chosen. */
function FilePick({ label, hint, files, error, multiple, onChoose }: { label: string; hint: string; files: File[]; error?: string; multiple?: boolean; onChoose: (files: File[]) => void }) {
  return (
    <label className={`flex cursor-pointer flex-wrap items-center gap-x-4 gap-y-2 rounded-md border bg-surface-card p-4 ${error ? "border-danger-500" : "border-border-subtle"}`}>
      <Icon icon={files.length ? CheckCircle2 : FileUp} size={22} className={files.length ? "text-success-700" : "text-ink-400"} />
      <span className="min-w-0 flex-1">
        <span className="block type-body-strong text-text-strong">{label}</span>
        <span className="block type-small text-text-muted">{files.length ? files.map((file) => file.name).join(", ") : hint}</span>
        {error && <span role="alert" className="block type-small text-danger-700">{error}</span>}
      </span>
      <span className="type-label text-text-link">{files.length ? "Change" : "Choose file"}</span>
      <input
        type="file"
        accept="image/*,application/pdf"
        multiple={multiple}
        className="sr-only"
        onChange={(event) => {
          onChoose(Array.from(event.target.files ?? []));
          event.target.value = "";
        }}
      />
    </label>
  );
}
